import { debounce } from 'lodash-es';
import { defineStore } from 'pinia';
import type { TableSettingCache } from '@mts/components/Table';
import type { Nullable } from '@mts-fe/types';
import { TABLE_SETTING_KEY } from '@mts/enums/cacheEnum';
import { Persistent } from '@mts/utils/cache/persistent';
import API, { parseConfigValue } from '@/api/ims-system';

interface TableSettingState {
  setting: Nullable<Recordable<Partial<TableSettingCache>>>;
}

export const useTableSettingStore = defineStore('table-setting', {
  state: (): TableSettingState => ({
    setting: Persistent.getLocal(TABLE_SETTING_KEY),
  }),
  getters: {
    getTableSize(state) {
      return (tableId: string) => state.setting?.[tableId]?.size || 'middle';
    },
    getShowIndexColumn(state) {
      return (tableId: string) => state.setting?.[tableId]?.showIndexColumn;
    },
    getShowRowSelection(state) {
      return (tableId: string) => state.setting?.[tableId]?.showRowSelection;
    },
    getShowActionColumn(state) {
      return (tableId: string) => state.setting?.[tableId]?.showActionColumn;
    },
    getColumns(state) {
      return (tableId: string) => state.setting?.[tableId]?.columns;
    },
  },
  actions: {
    setTableSetting(tableId: string, setting: Partial<TableSettingCache>, remote = true) {
      const tableSetting = { ...this.setting?.[tableId], ...setting };
      this.setting = Object.assign({}, this.setting, { [tableId]: tableSetting });
      Persistent.setLocal(TABLE_SETTING_KEY, this.setting, true);

      remote && saveTableSetting(tableId, tableSetting);
    },
    deleteTableSetting(tableId: string) {
      this.setting![tableId] = {};
      saveTableSetting(tableId, {});
    },
    resetTableSetting() {
      Persistent.removeLocal(TABLE_SETTING_KEY, true);
      this.setting = null;
    },
  },
});

/** 获取用户表格缓存的配置 */
export const loadTableSetting = (configKey) =>
  API.getSysConfig({ configKey }).then(
    parseConfigValue,
  ) as Promise<Partial<TableSettingCache> | void>;

/** 缓存用户表格配置 */
export const saveTableSetting = debounce(
  (configKey: string, tableSetting: Partial<TableSettingCache>) => {
    API.saveSysConfig({
      usageScene: 'table_cache',
      configKey,
      configValue: JSON.stringify(tableSetting),
    });
  },
  300,
  { trailing: true },
);
